import { useCallback, useEffect, useRef, useState } from 'react';
import {
  CrossTabCommand,
  WACallStates,
  WACallStatus,
  WAStorageState,
  WA_BROADCAST_CHANNEL,
  WA_STORAGE_KEY
} from './interface';
import {
  clearStoredState,
  isStoredStateStale,
  readStoredState,
  updateHeartbeat
} from './callStateStorage';
import { isBlockingStatus, isCrossTabActiveStatus } from './utils';
import { getTabId } from './utils';

interface UseCrossTabSyncParams {
  loanId: string;
  customerId: string;
  currentStatus: WACallStatus;
  onStateSync: (stored: WAStorageState) => void;
  onStateCleared: () => void;
  onDisconnectCommand: () => void;
}

export interface CrossTabSyncResult {
  isCallActiveElsewhere: boolean;
  isSameCustomerCall: boolean;
  isOwnTabCallForDifferentCustomer: boolean;
  sendDisconnectCommand: () => void;
  refreshFlags: () => void;
}

export const useCrossTabSync = ({
  loanId,
  customerId,
  currentStatus,
  onStateSync,
  onStateCleared,
  onDisconnectCommand
}: UseCrossTabSyncParams): CrossTabSyncResult => {
  const [isCallActiveElsewhere, setIsCallActiveElsewhere] = useState<boolean>(() => {
    const stored = readStoredState();
    if (!stored || stored.ownerTabId === getTabId() || isStoredStateStale(stored)) return false;
    const active = isCrossTabActiveStatus(stored.status);
    const sameCtx = stored.loanId === loanId && stored.customerId === customerId;
    return active && !sameCtx;
  });

  const [isSameCustomerCall, setIsSameCustomerCall] = useState<boolean>(() => {
    const stored = readStoredState();
    if (!stored || stored.ownerTabId === getTabId() || isStoredStateStale(stored)) return false;
    return (
      isCrossTabActiveStatus(stored.status) &&
      stored.loanId === loanId &&
      stored.customerId === customerId
    );
  });

  const [isOwnTabCallForDifferentCustomer, setIsOwnTabCallForDifferentCustomer] = useState<boolean>(
    () => {
      const stored = readStoredState();
      if (!stored || stored.ownerTabId !== getTabId() || isStoredStateStale(stored)) return false;
      const active = isCrossTabActiveStatus(stored.status);
      const sameCtx = stored.loanId === loanId && stored.customerId === customerId;
      return active && !sameCtx;
    }
  );

  const onStateSyncRef = useRef(onStateSync);
  onStateSyncRef.current = onStateSync;
  const onStateClearedRef = useRef(onStateCleared);
  onStateClearedRef.current = onStateCleared;
  const onDisconnectCommandRef = useRef(onDisconnectCommand);
  onDisconnectCommandRef.current = onDisconnectCommand;
  const currentStatusRef = useRef(currentStatus);
  currentStatusRef.current = currentStatus;
  const loanIdRef = useRef(loanId);
  loanIdRef.current = loanId;
  const customerIdRef = useRef(customerId);
  customerIdRef.current = customerId;

  const refreshFlags = useCallback(() => {
    const stored = readStoredState();
    if (!stored || isStoredStateStale(stored)) {
      setIsCallActiveElsewhere(false);
      setIsSameCustomerCall(false);
      setIsOwnTabCallForDifferentCustomer(false);
      return;
    }

    const active = isCrossTabActiveStatus(stored.status);
    const sameCtx =
      stored.loanId === loanIdRef.current && stored.customerId === customerIdRef.current;

    if (stored.ownerTabId === getTabId()) {
      setIsCallActiveElsewhere(false);
      setIsSameCustomerCall(false);
      setIsOwnTabCallForDifferentCustomer(active && !sameCtx);
    } else {
      setIsCallActiveElsewhere(active && !sameCtx);
      setIsSameCustomerCall(active && sameCtx);
      setIsOwnTabCallForDifferentCustomer(false);
    }
  }, []);

  // Storage event listener — reacts to changes made by other tabs
  useEffect(() => {
    const handleStorageChange = (event: StorageEvent) => {
      if (event.key !== WA_STORAGE_KEY) return;
      if (!event.newValue) {
        setIsCallActiveElsewhere(false);
        setIsSameCustomerCall(false);
        setIsOwnTabCallForDifferentCustomer(false);
        if (currentStatusRef.current !== WACallStates.IDLE) {
          onStateClearedRef.current();
        }
        return;
      }

      try {
        const stored: WAStorageState = JSON.parse(event.newValue);
        if (stored.ownerTabId === getTabId()) return;

        const active = isCrossTabActiveStatus(stored.status);
        const sameCtx =
          stored.loanId === loanIdRef.current && stored.customerId === customerIdRef.current;

        setIsCallActiveElsewhere(active && !sameCtx);
        setIsSameCustomerCall(active && sameCtx);

        if (sameCtx && active) {
          onStateSyncRef.current(stored);
        }

        if (sameCtx && !active && currentStatusRef.current !== WACallStates.IDLE) {
          onStateClearedRef.current();
        }
      } catch {
        /* ignore parse errors */
      }
    };

    window.addEventListener('storage', handleStorageChange);
    return () => window.removeEventListener('storage', handleStorageChange);
  }, []);

  // Owner tab: heartbeat to let non-owner tabs know the session is alive.
  useEffect(() => {
    const stored = readStoredState();
    const isOwner = stored?.ownerTabId === getTabId();
    if (!isOwner) return;

    const localActive = isBlockingStatus(currentStatus);
    const storedActive = stored && isBlockingStatus(stored.status);
    if (!localActive && !storedActive) return;

    updateHeartbeat();
    const interval = setInterval(updateHeartbeat, 10_000);
    return () => clearInterval(interval);
  }, [currentStatus, loanId, customerId]);

  // Non-owner tabs: poll to detect stale (crashed owner) state
  useEffect(() => {
    if (!isCallActiveElsewhere && !isSameCustomerCall) return;

    const interval = setInterval(() => {
      const stored = readStoredState();
      if (!stored || isStoredStateStale(stored)) {
        clearStoredState();
        setIsCallActiveElsewhere(false);
        setIsSameCustomerCall(false);
        if (currentStatusRef.current !== WACallStates.IDLE) {
          onStateClearedRef.current();
        }
      }
    }, 15_000);

    return () => clearInterval(interval);
  }, [isCallActiveElsewhere, isSameCustomerCall]);

  // Owner tab: BroadcastChannel listener for commands from viewer tabs
  useEffect(() => {
    if (typeof BroadcastChannel === 'undefined') return;

    const channel = new BroadcastChannel(WA_BROADCAST_CHANNEL);
    channel.onmessage = (event: MessageEvent<CrossTabCommand>) => {
      if (event.data?.type === 'disconnect') {
        onDisconnectCommandRef.current();
      }
    };
    return () => channel.close();
  }, []);

  const sendDisconnectCommand = useCallback(() => {
    if (typeof BroadcastChannel === 'undefined') return;
    const channel = new BroadcastChannel(WA_BROADCAST_CHANNEL);
    const command: CrossTabCommand = { type: 'disconnect', fromTabId: getTabId() };
    channel.postMessage(command);
    channel.close();
  }, []);

  return {
    isCallActiveElsewhere,
    isSameCustomerCall,
    isOwnTabCallForDifferentCustomer,
    sendDisconnectCommand,
    refreshFlags
  };
};
